import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { resolveShareToken, installTool, installToolForAgency, type ResolvedShare } from '../../services/myToolsApi';
import McpDetailBody from '../../components/McpDetailBody';
import ShareResolveDetailView from '../../components/ShareResolveDetailView';
import IntegrationDetailBody from '../apiintegrations/IntegrationDetailBody';
import { IntegrationKeyInput } from '../apiintegrations/IntegrationKeyPanel';
import { AgentEnablementPanel } from '../apiintegrations/AgentEnablementPanel';
import { getMyCredential, saveMyCredential, type InstallerCredentialStatus } from '../apiintegrations/apiIntegrationService';
import { ROUTES } from '../../app/routes';
import '../../styles/MyTools.css';
type Status = 'loading' | 'preview' | 'installing' | 'installed' | 'error';
const TOOL_TYPE_LABELS: Record<ResolvedShare['toolType'], string> = {
    skill: 'Skill', agent: 'Agent', workflow: 'Workflow', widget: 'Widget', mcp_server: 'MCP Server', integration: 'Integration',
};

/** After an integration is installed from a link: add the API key it needs (if any),
 *  then turn it on for agents - the same two steps a Marketplace install ends with. */
const IntegrationAfterInstall: React.FC<{ integrationId: number }> = ({ integrationId }) => {
    const [status, setStatus] = useState<InstallerCredentialStatus | null>(null);
    const [key, setKey] = useState('');
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
    useEffect(() => {
        getMyCredential(integrationId).then(setStatus).catch(() => setStatus(null));
    }, [integrationId]);
    const save = async () => {
        setSaving(true);
        setMessage(null);
        try {
            setStatus(await saveMyCredential(integrationId, key.trim(), status?.target ?? 'me'));
            setKey('');
            setMessage({ ok: true, text: 'Key saved.' });
        }
        catch (err: any) {
            setMessage({ ok: false, text: err.message || "Couldn't save the key." });
        }
        finally {
            setSaving(false);
        }
    };
    const needsKey = !!status?.required && !status.set;
    return (<div className="mt-detail-card" style={{ width: '100%', maxWidth: 560, textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {status?.required && (<div>
          <h4 className="mt-detail-section-title" style={{ marginTop: 0 }}>API key</h4>
          {needsKey && status.can_edit ? (<>
              <p className="mt-step-subtitle" style={{ margin: '0 0 8px' }}>
                This API needs a key before calls work. Calls from you use your own key - it's never used by anyone else, not even the person who shared this.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <IntegrationKeyInput id="share-install-key" value={key} onChange={setKey} disabled={saving}/>
                <button type="button" className="mt-btn mt-btn--primary mt-btn--sm" onClick={save} disabled={saving || !key.trim()}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
              <p className="mt-field-hint" style={{ marginTop: 6 }}>You can also add it later from Marketplace &gt; Installed.</p>
            </>) : needsKey ? (<p className="mt-step-subtitle" style={{ margin: 0 }}>Your Agency Admin adds the key for your agency.</p>)
                : (<p className="mt-step-subtitle" style={{ margin: 0 }}>
                  {status.source === 'agency' ? "Your agency's key" : 'Your key'} ending in <span className="ai-mono">{status.last4}</span> is saved.
                </p>)}
          {message && <p className={message.ok ? 'ai-success-text' : 'ai-error'} role={message.ok ? 'status' : 'alert'}>{message.text}</p>}
        </div>)}
      <div>
        <h4 className="mt-detail-section-title" style={{ marginTop: 0 }}>Turn it on for your agents</h4>
        <AgentEnablementPanel integrationId={integrationId}/>
      </div>
    </div>);
};

const ShareResolvePage: React.FC = () => {
    const { token } = useParams<{
        token: string;
    }>();
    const navigate = useNavigate();
    const [status, setStatus] = useState<Status>('loading');
    const [error, setError] = useState('');
    const [resolved, setResolved] = useState<ResolvedShare | null>(null);
    const [installingForAgency, setInstallingForAgency] = useState(false);
    const [agencyInstalled, setAgencyInstalled] = useState(false);
    const [trusted, setTrusted] = useState(false);
    // An MCP server's installer-supplied values, collected before install exactly like
    // a Marketplace install - without them the install is refused.
    const [values, setValues] = useState<Record<string, string>>({});
    // Loading -> Preview only resolves the share (minting/looking up the
    // independent copy server-side) and shows what it contains — nothing is
    // installed onto this account yet. Install only happens once the user
    // confirms via handleConfirm.
    const load = useCallback(async () => {
        if (!token)
            return;
        setStatus('loading');
        setError('');
        try {
            const share = await resolveShareToken(token);
            setResolved(share);
            setStatus('preview');
        }
        catch (err: any) {
            setError(err.message || 'This link could not be opened.');
            setStatus('error');
        }
    }, [token]);
    useEffect(() => { load(); }, [load]);
    const fields = resolved?.toolType === 'mcp_server' ? resolved.mcpInstallerFields : [];
    const fieldsFilled = fields.every(f => (values[f.key] ?? '').trim());
    const handleConfirm = async () => {
        if (!resolved)
            return;
        setStatus('installing');
        setError('');
        try {
            await installTool(resolved.copiedToolId, fields.length ? values : undefined, 'share_link');
        }
        catch (err: any) {
            if (err.message !== 'already_installed') {
                setError(err.message || 'This link could not be opened.');
                setStatus('error');
                return;
            }
        }
        setStatus('installed');
    };
    const handleInstallForAgency = async () => {
        if (!resolved)
            return;
        setInstallingForAgency(true);
        try {
            // An agency install takes the shared values; per-user ones each person adds later.
            const shared = Object.fromEntries(fields.filter(f => f.scope === 'shared').map(f => [f.key, values[f.key] ?? '']));
            await installToolForAgency(resolved.copiedToolId, fields.length ? shared : undefined);
            setAgencyInstalled(true);
        }
        catch (err: any) {
            setError(err.message || 'Failed to install for your agency.');
        }
        finally {
            setInstallingForAgency(false);
        }
    };
    if (status === 'loading' || status === 'installing') {
        return (<div className="mt-page">
        <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                minHeight: '60vh', textAlign: 'center', gap: 14,
            }}>
          <div style={{ fontSize: 32 }}>⏳</div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            {status === 'loading' ? 'Opening shared link…' : 'Installing…'}
          </p>
        </div>
      </div>);
    }
    if (status === 'preview' && resolved) {
        return (<div className="mt-page">
        <ShareResolveDetailView icon={resolved.toolIcon || '📦'} name={resolved.toolName} typeLabel={TOOL_TYPE_LABELS[resolved.toolType]} category={resolved.toolCategory} description={resolved.toolDescription}>
          {resolved.toolType === 'mcp_server' && (<>
            <h4 className="mt-detail-section-title">MCP Server</h4>
            <McpDetailBody url={resolved.mcpUrl} headers={resolved.mcpHeaderKeys.map(key => ({ key }))}/>
          </>)}
          {resolved.toolType === 'integration' && (<>
            <h4 className="mt-detail-section-title">API Integration</h4>
            <IntegrationDetailBody apiIntegrationId={resolved.apiIntegrationId}/>
          </>)}
        </ShareResolveDetailView>

        <div className="mt-detail-card" style={{ marginTop: 14, marginBottom: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {fields.length > 0 && (<div className="mt-form">
              <p className="mt-step-subtitle" style={{ margin: 0 }}>This tool needs a few values from you before it can connect.</p>
              {fields.map(f => (<div key={f.key} className="mt-field mt-field--full">
                  <label className="mt-label" htmlFor={`share-field-${f.key}`}>{f.label} <span className="mt-required">*</span></label>
                  {f.hint && <p className="mt-field-hint">{f.hint}</p>}
                  <input id={`share-field-${f.key}`} className="mt-input" type="text" autoComplete="off" value={values[f.key] ?? ''}
                    onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}/>
                </div>))}
            </div>)}
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={trusted} onChange={e => setTrusted(e.target.checked)} style={{ marginTop: 2 }}/>
            I trust this community shared item and understand it comes from another agency in the Momentum AMP community, not from Momentum AMP directly.
          </label>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="mt-btn mt-btn--primary" disabled={!trusted || !fieldsFilled} onClick={handleConfirm}>
              Install
            </button>
          </div>
        </div>
      </div>);
    }
    return (<div className="mt-page">
      <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            minHeight: '60vh', textAlign: 'center', gap: 14,
        }}>
        {status === 'installed' && resolved ? (<>
            <div style={{ fontSize: 32 }}>✅</div>
            <h2 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: 0 }}>
              "{resolved.toolName}" installed
            </h2>
            {resolved.toolDescription && (<p style={{ color: 'var(--text-secondary)', fontSize: 13, maxWidth: 420 }}>{resolved.toolDescription}</p>)}
            {resolved.toolType === 'integration' && resolved.apiIntegrationId && (<IntegrationAfterInstall integrationId={resolved.apiIntegrationId}/>)}
            {error && <p className="ai-error" role="alert">{error}</p>}
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              {resolved.isAgencyOwner && !agencyInstalled && (<button type="button" className="mt-btn mt-btn--secondary" onClick={handleInstallForAgency} disabled={installingForAgency}>
                  {installingForAgency ? 'Installing…' : 'Install for Agency'}
                </button>)}
              {agencyInstalled && <span style={{ color: 'var(--text-secondary)', fontSize: 13, alignSelf: 'center' }}>Installed for your agency ✓</span>}
              <button type="button" className="mt-btn mt-btn--primary" onClick={() => navigate(`${ROUTES.MY_TOOLS}?view=installed`)}>
                Go to My Tools
              </button>
            </div>
          </>) : (<>
            <div style={{ fontSize: 32 }}>⚠️</div>
            <h2 style={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 700, margin: 0 }}>Couldn't open this link</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, maxWidth: 420 }}>{error}</p>
          </>)}
      </div>
    </div>);
};
export default ShareResolvePage;
